import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Button, ProgressBar } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { Server, Shield, Zap, Globe, BarChart, RefreshCw, Lock, Database, Layers } from 'lucide-react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../styles/services/solutions/infrapage.css';

const CloudInfrastructure = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  return (
    <div className="cloud-infrastructure-page">
      {/* Hero Section */}
      <motion.div 
        className="hero-section4 text-white"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Container>
          <Row className="align-items-center min-vh-80">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
              >
                <h1 className="display-3 fw-bold mb-4">Cloud Infrastructure Solutions</h1>
                <p className="lead mb-4">Scalable, secure cloud solutions and migration services to power your digital transformation.</p>
                <Button variant="light" size="lg" className="me-3 mb-3">Get Started</Button>
                <Button variant="outline-light" size="lg" className="mb-3">Schedule Consultation</Button>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                className="cloud-animation"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                <div className="server-rack-container">
                  <img 
                    src="/api/placeholder/600/400" 
                    alt="Cloud Infrastructure Illustration" 
                    className="img-fluid rounded shadow-lg"
                  />
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      {/* Solutions Section */}
      <section className="solutions-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Our Cloud Solutions</h2>
            <p className="lead text-muted">Comprehensive services to meet all your cloud infrastructure needs</p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <Row className="g-4">
              {[
                { 
                  icon: <Server size={40} />, 
                  title: "Cloud Hosting", 
                  description: "Reliable, high-performance hosting with 99.9% uptime guarantee and automatic scaling to handle traffic spikes." 
                },
                { 
                  icon: <Layers size={40} />, 
                  title: "Infrastructure as a Service (IaaS)", 
                  description: "Fully managed infrastructure with virtual machines, storage, networking, and more." 
                },
                { 
                  icon: <Globe size={40} />, 
                  title: "Global Content Delivery", 
                  description: "Edge network ensures fast loading times for users worldwide with intelligent caching and routing." 
                },
                { 
                  icon: <Database size={40} />, 
                  title: "Database Solutions", 
                  description: "Managed relational, NoSQL, and in-memory databases optimized for performance and availability." 
                },
                { 
                  icon: <Shield size={40} />, 
                  title: "Security & Compliance", 
                  description: "Enterprise-grade security with threat detection, encryption, and compliance certifications." 
                },
                { 
                  icon: <RefreshCw size={40} />, 
                  title: "Disaster Recovery", 
                  description: "Automated backup and recovery solutions with minimal downtime and data loss." 
                }
              ].map((solution, index) => (
                <Col md={6} lg={4} key={index}>
                  <motion.div
                    variants={fadeIn}
                  >
                    <Card className="solution-card h-100 shadow border-0 hover-lift">
                      <Card.Body className="p-4">
                        <div className="solution-icon mb-4 text-primary">
                          {solution.icon}
                        </div>
                        <h4 className="fw-bold mb-3">{solution.title}</h4>
                        <p className="text-muted mb-4">{solution.description}</p>
                        <Button variant="outline-primary" className="stretched-link">Learn More</Button>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Cloud Benefits Section */}
      <section className="benefits-section py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-5 fw-bold mb-4">Why Choose Our Cloud Solutions?</h2>
                <p className="lead mb-4">Experience the benefits of a modern cloud infrastructure designed for performance, security, and scalability.</p>
                
                <div className="benefits-list">
                  {[
                    { 
                      icon: <Zap size={24} />, 
                      title: "Enhanced Performance", 
                      description: "Optimized infrastructure delivers faster application response times and improved user experiences." 
                    },
                    { 
                      icon: <Lock size={24} />, 
                      title: "Enterprise-Grade Security", 
                      description: "Multi-layered security approach with encryption, firewalls, and 24/7 monitoring." 
                    },
                    { 
                      icon: <BarChart size={24} />, 
                      title: "Scalable Resources", 
                      description: "Dynamically adjust resources to meet changing demand without service interruptions." 
                    },
                    { 
                      icon: <RefreshCw size={24} />, 
                      title: "Business Continuity", 
                      description: "Redundant systems and automated backups ensure your business stays operational." 
                    }
                  ].map((benefit, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2, duration: 0.6 }}
                      viewport={{ once: true }}
                      className="benefit-item d-flex mb-4"
                    >
                      <div className="benefit-icon me-3 text-primary">
                        {benefit.icon}
                      </div>
                      <div className="benefit-content">
                        <h5 className="fw-bold mb-2">{benefit.title}</h5>
                        <p className="text-muted mb-0">{benefit.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </Col>
            <Col lg={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="benefits-image-container"
              >
                <img 
                  src="/api/placeholder/600/400" 
                  alt="Cloud Benefits" 
                  className="img-fluid rounded shadow-lg"
                />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Migration Process Section */}
      <section className="migration-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Seamless Cloud Migration</h2>
            <p className="lead text-muted">Our proven four-step process ensures a smooth transition to the cloud</p>
          </motion.div>

          <Row className="g-4">
            {[
              {
                step: "1",
                title: "Assessment & Planning",
                description: "We analyze your current infrastructure and develop a comprehensive migration strategy tailored to your needs.",
                progress: 100
              },
              {
                step: "2",
                title: "Architecture Design",
                description: "Our experts design a cloud architecture optimized for performance, security, and cost-efficiency.",
                progress: 100
              },
              {
                step: "3",
                title: "Migration Execution",
                description: "We implement the migration with minimal disruption to your operations, following industry best practices.",
                progress: 100
              },
              {
                step: "4",
                title: "Optimization & Support",
                description: "Post-migration, we continually optimize your infrastructure and provide ongoing technical support.",
                progress: 100
              }
            ].map((step, index) => (
              <Col md={6} lg={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  viewport={{ once: true }}
                  className="migration-step-card h-100"
                >
                  <div className="step-number mb-4">
                    <span>{step.step}</span>
                  </div>
                  <h4 className="fw-bold mb-3">{step.title}</h4>
                  <p className="text-muted mb-3">{step.description}</p>
                  <ProgressBar now={step.progress} variant="primary" className="mt-auto" />
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section py-5 bg-light">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">What Our Clients Say</h2>
            <p className="lead text-muted">Trusted by leading companies worldwide</p>
          </motion.div>

          <Row className="g-4">
            {[
              {
                quote: "The cloud migration was seamless and the performance improvements have been substantial. Our applications are now faster and more reliable than ever.",
                name: "Sarah Johnson",
                title: "CTO, TechCorp Solutions",
                image: "/api/placeholder/80/80"
              },
              {
                quote: "Their expertise in cloud infrastructure saved us countless hours and resources. The security features implemented give us peace of mind about our data protection.",
                name: "Michael Chen",
                title: "VP of Engineering, DataStream Inc.",
                image: "/api/placeholder/80/80"
              },
              {
                quote: "We've reduced our infrastructure costs by 40% while improving performance and scalability. The ongoing support has been exceptional.",
                name: "Emily Rodriguez",
                title: "IT Director, Global Enterprises",
                image: "/api/placeholder/80/80"
              }
            ].map((testimonial, index) => (
              <Col md={6} lg={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <Card className="testimonial-card h-100 shadow-sm border-0">
                    <Card.Body className="p-4">
                      <div className="testimonial-quote mb-4">
                        <i className="bi bi-quote"></i>
                        <p className="fst-italic mb-0">{testimonial.quote}</p>
                      </div>
                      <div className="testimonial-author d-flex align-items-center">
                        <div className="author-image me-3">
                          <img 
                            src={testimonial.image} 
                            alt={testimonial.name} 
                            className="rounded-circle"
                            width="50" 
                          />
                        </div>
                        <div className="author-info">
                          <h5 className="fw-bold mb-1">{testimonial.name}</h5>
                          <p className="text-muted small mb-0">{testimonial.title}</p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5 text-white">
        <Container>
          <Row className="justify-content-center">
            <Col lg={10}>
              <motion.div
                className="text-center py-5"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="display-4 fw-bold mb-4">Ready to Transform Your Cloud Infrastructure?</h2>
                <p className="lead mb-5">Get started today with a free consultation and personalized cloud assessment</p>
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                  <Button variant="light" size="lg" className="px-4">Schedule Consultation</Button>
                  <Button variant="outline-light" size="lg" className="px-4">View Pricing</Button>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section py-5">
        <Container>
          <motion.div
            className="text-center mb-5"
            variants={fadeIn}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <h2 className="display-5 fw-bold">Frequently Asked Questions</h2>
            <p className="lead text-muted">Answers to common questions about our cloud infrastructure solutions</p>
          </motion.div>

          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="accordion" id="faqAccordion">
                {[
                  {
                    question: "How long does the cloud migration process take?",
                    answer: "The timeline varies depending on the complexity of your infrastructure, but most migrations are completed in 2-6 weeks. Our team works closely with you to establish a realistic timeline based on your specific needs and constraints."
                  },
                  {
                    question: "Is my data secure during and after migration?",
                    answer: "Absolutely. Security is our top priority. We implement multiple layers of security including encryption, access controls, and continuous monitoring. All migrations follow industry best practices and compliance requirements to ensure your data remains protected throughout the process."
                  },
                  {
                    question: "How do you minimize downtime during migration?",
                    answer: "We use a phased migration approach that minimizes disruptions to your operations. This includes parallel environments, incremental data transfers, and scheduled cutover during off-peak hours. Our goal is to achieve near-zero downtime migration whenever possible."
                  },
                  {
                    question: "What ongoing support do you provide post-migration?",
                    answer: "We offer comprehensive support packages including 24/7 monitoring, regular maintenance, security updates, performance optimization, and technical support. Our team becomes an extension of your IT department, ensuring your cloud infrastructure operates at peak efficiency."
                  },
                  {
                    question: "How will moving to the cloud impact our costs?",
                    answer: "Most clients see a significant reduction in total cost of ownership (TCO) after migrating to the cloud. We provide detailed cost projections during the assessment phase and implement cost optimization strategies to ensure you get the most value from your cloud investment."
                  }
                ].map((faq, index) => (
                  <motion.div 
                    key={index}
                    className="accordion-item border-0 mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    viewport={{ once: true }}
                  >
                    <h3 className="accordion-header" id={`heading${index}`}>
                      <button 
                        className="accordion-button collapsed fw-bold" 
                        type="button" 
                        data-bs-toggle="collapse" 
                        data-bs-target={`#collapse${index}`} 
                        aria-expanded="false" 
                        aria-controls={`collapse${index}`}
                      >
                        {faq.question}
                      </button>
                    </h3>
                    <div 
                      id={`collapse${index}`} 
                      className="accordion-collapse collapse" 
                      aria-labelledby={`heading${index}`} 
                      data-bs-parent="#faqAccordion"
                    >
                      <div className="accordion-body text-muted">
                        {faq.answer}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default CloudInfrastructure;